import { useState } from "react"
import { Minus, Plus } from "lucide-react"

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { cn } from "@/lib/utils"
import { Panel, SectionHead } from "./Panel"
import { formatDollars } from "./format"

export const WORD_OPTIONS = [20_000, 30_000, 50_000, 80_000]
export const MIN_BOOKS = 1
export const MAX_BOOKS = 24

const SHORT_LABELS = { "Finance and Technology": "Finance & Tech" }

// A year of writing at one tier, standard vs Insider, with the membership
// counted. `row` is a book row from the presenter (cents for `word_count`
// words); everything stays in cents. breakEven is null when Insider saves
// nothing per book at today's prices.
// Multiply before dividing, so whole-cent inputs give exact results and the
// break-even never rounds up off a float remainder.
export function calcSavings({ row, words, books, planCents }) {
  const standard = (row.standard_cents * words * books) / row.word_count
  const insider = (row.insider_cents * words * books) / row.word_count
  const fee = planCents * 12
  const perBook = ((row.standard_cents - row.insider_cents) * words) / row.word_count
  return {
    standard,
    insider,
    fee,
    perBook,
    net: standard - insider - fee,
    breakEven: perBook > 0 ? Math.ceil(fee / perBook) : null,
  }
}

function Field({ label, id, children }) {
  return (
    <div>
      <div id={id} className="mb-[9px] text-[10.5px] font-bold tracking-[0.08em] text-ink-3 uppercase">
        {label}
      </div>
      {children}
    </div>
  )
}

function Bar({ label, total, parts, max }) {
  const width = (v) => `${((v / max) * 100).toFixed(1)}%`
  return (
    <div>
      <div className="mb-[6px] flex justify-between gap-[10px] text-[12.5px] text-muted-foreground tabular-nums">
        <span className="min-w-0 truncate">{label}</span>
        <b className="shrink-0 font-bold text-foreground">{formatDollars(total)}</b>
      </div>
      <div className="flex h-[10px] overflow-hidden rounded-[6px] bg-secondary" aria-hidden>
        {parts.map((p) => (
          <div key={p.key} className={cn("h-full transition-[width] duration-250 motion-reduce:transition-none", p.className)} style={{ width: width(p.value) }} />
        ))}
      </div>
    </div>
  )
}

function Stepper({ value, onChange }) {
  const step = "flex size-10 items-center justify-center text-foreground hover:bg-secondary disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent"
  return (
    <div className="inline-flex items-center overflow-hidden rounded-[10px] border border-line-2 bg-card">
      <button type="button" aria-label="Fewer books" className={step} disabled={value <= MIN_BOOKS} onClick={() => onChange(value - 1)}>
        <Minus className="size-4" strokeWidth={2.2} />
      </button>
      <output aria-live="polite" aria-label="Books per year" className="min-w-[46px] text-center font-display text-[16px] font-bold text-foreground">
        {value}
      </output>
      <button type="button" aria-label="More books" className={step} disabled={value >= MAX_BOOKS} onClick={() => onChange(value + 1)}>
        <Plus className="size-4" strokeWidth={2.2} />
      </button>
    </div>
  )
}

// "Is it worth it for you?": a pure function of the book rows and the plan
// price, so it always matches the price table below it. Writing cost only.
export default function SavingsCalculator({ comparison, plan }) {
  const rows = (comparison?.bookRows ?? []).filter((r) => r.standard_cents != null && r.insider_cents != null && r.word_count)
  const [tier, setTier] = useState(rows[0]?.label)
  const [words, setWords] = useState(30_000)
  const [books, setBooks] = useState(4)
  const row = rows.find((r) => r.label === tier) ?? rows[0]
  if (!row || plan?.amountCents == null) return null

  const r = calcSavings({ row, words, books, planCents: plan.amountCents })
  const max = Math.max(r.standard, r.insider + r.fee)
  const saves = r.net >= 0

  return (
    <>
      <SectionHead id="calc" title="Is it worth it for you?">
        Insider costs {formatDollars(r.fee)} a year. Set your usual order to see whether the savings cover it.
      </SectionHead>
      <Panel className="grid grid-cols-2 items-stretch overflow-hidden max-[1100px]:grid-cols-1">
        <div className="flex flex-col gap-[22px] px-[28px] py-[26px] max-[861px]:px-[20px] max-[861px]:py-[22px] max-[561px]:px-[18px] max-[561px]:py-[20px]">
          <Field label="Writing tier" id="calc-tier">
            <ToggleGroup type="single" aria-labelledby="calc-tier" value={row.label} onValueChange={(v) => v && setTier(v)}>
              {rows.map((t) => (
                <ToggleGroupItem key={t.label} value={t.label}>
                  {SHORT_LABELS[t.label] ?? t.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Field>
          <Field label="Words per book" id="calc-words">
            <ToggleGroup type="single" aria-labelledby="calc-words" value={String(words)} onValueChange={(v) => v && setWords(Number(v))}>
              {WORD_OPTIONS.map((w) => (
                <ToggleGroupItem key={w} value={String(w)}>
                  {w / 1000}k
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Field>
          <Field label="Books per year" id="calc-books">
            <div className="flex items-center gap-[12px]">
              <Stepper value={books} onChange={setBooks} />
              <span className="text-[12.5px] text-ink-3">{formatDollars(r.perBook)} saved per book</span>
            </div>
          </Field>
        </div>

        <div className="flex flex-col gap-[16px] border-l border-border bg-surface3 px-[28px] py-[26px] max-[1100px]:border-t max-[1100px]:border-l-0 max-[861px]:px-[20px] max-[861px]:py-[22px] max-[561px]:px-[18px] max-[561px]:py-[20px]">
          <div aria-live="polite">
            <div className="text-[10.5px] font-bold tracking-[0.08em] text-ink-3 uppercase">
              {saves ? "Insider saves you" : "Insider would cost you"}
            </div>
            <div className={cn("mt-[6px] font-display text-[40px] leading-[1.15] font-bold tracking-[-1.3px] tabular-nums max-[561px]:text-[32px]", saves ? "text-done" : "text-foreground")}>
              {saves ? formatDollars(r.net) : `${formatDollars(-r.net)} more`}
            </div>
            <p className="mt-[6px] text-[13px] leading-[1.5] text-muted-foreground">
              {saves
                ? `saved per year after the ${formatDollars(r.fee)} membership.`
                : `per year than standard pricing, once the ${formatDollars(r.fee)} membership is counted.`}
            </p>
            <p className="mt-[6px] text-[13px] leading-[1.5] text-muted-foreground">
              {r.breakEven ? (
                <>
                  Breaks even at{" "}
                  <b className="text-foreground">
                    {r.breakEven} book{r.breakEven !== 1 ? "s" : ""} a year
                  </b>{" "}
                  at this tier and length.
                </>
              ) : (
                "This tier costs the same on both price lists today, so there is no break-even yet."
              )}
            </p>
          </div>
          <div className="flex flex-col gap-[12px]">
            <Bar label="Standard pricing" total={r.standard} max={max} parts={[{ key: "std", value: r.standard, className: "bg-ink-3" }]} />
            <Bar
              label="Insider + membership"
              total={r.insider + r.fee}
              max={max}
              parts={[
                { key: "ins", value: r.insider, className: "bg-foreground" },
                { key: "fee", value: r.fee, className: "bg-line-2" },
              ]}
            />
            <div className="flex flex-wrap gap-[14px] text-[11.5px] text-ink-3">
              <span className="inline-flex items-center gap-[5px]"><i className="size-[9px] rounded-[3px] bg-foreground" />Writing at Insider rates</span>
              <span className="inline-flex items-center gap-[5px]"><i className="size-[9px] rounded-[3px] bg-line-2" />Membership, 12 months</span>
            </div>
          </div>
          <p className="border-t border-border pt-[12px] text-[12px] leading-[1.5] text-ink-3">
            Writing cost only. Editing, covers, formatting and most other services also cost less on Insider, so your real
            savings can be higher.
          </p>
        </div>
      </Panel>
    </>
  )
}
